import { FiberManualRecord } from '@mui/icons-material';
import { Box, Chip, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import useActiveApplication from 'hooks/useActiveApplication';
import { RootState } from 'redux/store';
import { setSelectedApplication } from 'redux/v2/selectedApplication.reducer';

/* ── Helpers ── */
const formatStatus = (status: string) =>
    status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b(\w)/g, (m) => m.toUpperCase());

const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
        case 'SUCCESS':
        case 'APPROVED':
            return 'success';
        case 'FAIL':
        case 'REJECTED':
        case 'CLOSED':
            return 'error';
        case 'SUBMITTED':
        case 'READY_FOR_SUBMISSION':
            return 'info';
        case 'DOCUMENTS_IN_PROCESS':
        case 'OFFER':
        case 'COUNTEROFFER_CANDIDATE':
        case 'COUNTEROFFER_CONSULTANT':
        case 'OFFER_ACCEPTED':
        case 'AWAITING_CANDIDATE_SIGNATURE':
        case 'AWAITING_CONSULTANT_SIGNATURE':
        case 'AWAITING_DOCUMENT_LIST':
            return 'warning';
        default:
            return 'default';
    }
};

const statusDotColor: Record<string, string> = {
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6',
    default: '#9ca3af',
};

/* ── Component ── */
const ApplicationSelector = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { selectedApplication } = useSelector((state: RootState) => state.selectedApplication);
    const { applications, loading } = useActiveApplication();

    // Redux stores selectedApplication loosely ('' | number); app.id is number
    const selectedApp = applications.find((app) => String(app.id) === String(selectedApplication));

    const handleApplicationChange = (newApplicationId: number) => {
        dispatch(setSelectedApplication(newApplicationId));

        const currentPath = location.pathname;
        const visaProcessMatch = currentPath.match(/^\/visa-process\/(?:\d+|undefined)(\/.*)?$/);

        if (visaProcessMatch) {
            const subPath = visaProcessMatch[1] ?? '';
            navigate(`/visa-process/${newApplicationId}${subPath}`, { replace: true });
            return;
        }

        if (selectedApplication == null) {
            navigate(`/visa-process/${newApplicationId}`, { replace: true });
        }
    };

    if (loading) {
        return null;
    }

    return (
        <Box sx={{ px: 1.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <FormControl fullWidth size="small">
                <InputLabel id="application-select-label">Application</InputLabel>
                <Select
                    labelId="application-select-label"
                    id="application-select"
                    value={selectedApplication || ''}
                    label="Application"
                    onChange={(e) => handleApplicationChange(Number(e.target.value))}
                    renderValue={() => {
                        if (!selectedApp) return '';
                        const color = getStatusColor(selectedApp.application_status);
                        return (
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <FiberManualRecord
                                        sx={{
                                            fontSize: 8,
                                            color: statusDotColor[color],
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{ fontWeight: 600, lineHeight: 1.3 }}
                                    >
                                        {selectedApp.program_name}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: 0.75,
                                        pl: 1.75,
                                        minWidth: 0,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                        sx={{ fontSize: '0.688rem', flexShrink: 0 }}
                                    >
                                        #{selectedApp.id} · {selectedApp.country}
                                    </Typography>
                                    <Chip
                                        label={formatStatus(selectedApp.application_status)}
                                        size="small"
                                        color={getStatusColor(selectedApp.application_status)}
                                        variant="outlined"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.6rem',
                                            fontWeight: 600,
                                            maxWidth: '50%',
                                            '& .MuiChip-label': {
                                                px: 0.5,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        );
                    }}
                    MenuProps={{
                        slotProps: {
                            paper: {
                                sx: {
                                    maxHeight: 360,
                                    mt: 0.5,
                                    minWidth: 260,
                                },
                            },
                        },
                    }}
                    sx={{
                        bgcolor: 'background.paper',
                        '& .MuiSelect-select': { py: 1, pr: '32px !important' },
                    }}
                >
                    {applications.map((app, idx) => (
                        <MenuItem
                            key={app.id}
                            value={app.id}
                            sx={{
                                py: 1.5,
                                px: 2,
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                ...(idx < applications.length - 1 && {
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                }),
                                '&.Mui-selected': {
                                    bgcolor: 'primary.50',
                                    '&:hover': { bgcolor: 'primary.100' },
                                },
                            }}
                        >
                            {/* Row 1 — Program name */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    lineHeight: 1.4,
                                    mb: 0.5,
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {app.program_name}
                            </Typography>

                            {/* Row 2 — Meta + Status */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.688rem', flexShrink: 0 }}
                                >
                                    #{app.id} · {app.country}
                                </Typography>
                                <Chip
                                    label={formatStatus(app.application_status)}
                                    size="small"
                                    color={getStatusColor(app.application_status)}
                                    variant="outlined"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.625rem',
                                        fontWeight: 600,
                                        '& .MuiChip-label': { px: 0.75 },
                                    }}
                                />
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

export default ApplicationSelector;
